/**
 * 에러 객체에 누적 기록되는 진단용 메타데이터
 *
 * 각 레이어(API, 인증 인터셉터, 표시 컴포넌트)가 자기가 아는 정보를 누적하고,
 * 전송 직전 beforeSend 가 이것을 Sentry 태그로 변환한다.
 *
 * 메타 데이터는 외부로 노출되지 않도록 에러 객체에 non-enumerable 프로퍼티로 저장
 *
 */

const META_KEY = "__ctrl_error_meta__";
const CAPTURED_KEY = "__ctrl_captured__";

export type ApiTrack = "legacy" | "react-query";
export type ErrorSurface = "modal" | "boundary";

export type ApiMeta = {
  /** apiPath.json 템플릿 (실제 URL 아님(e.g. /api/users/{:id})) */
  path?: string;
  method?: string;
  status?: number;
  errorCode?: string;
  track?: ApiTrack;
  /** 실제 URL contexts로만 활용됨 */
  url?: string;
};

export type ErrorMeta = {
  api?: ApiMeta;
  auth?: { reason: "token_expired" | "no_permission" };
  ui?: { surface: ErrorSurface };
  render?: { component: string };
  perceived?: boolean;
  /** 검색이 필요 없는 부가 정보. 태그가 아니라 contexts 로 나가고 스크러빙을 그대로 받는다 */
  extra?: Record<string, unknown>;
};

type MetaHolder = {
  __ctrl_error_meta__?: ErrorMeta;
  __ctrl_captured__?: boolean;
  __sentry_captured__?: boolean;
};

const isObject = (value: unknown): value is object => typeof value === "object" && value !== null;

const asHolder = (value: object): MetaHolder => value as MetaHolder;

/**
 * 메타 데이터를 첨부할 수 있는 에러인지 확인
 * primitive값, freeze 또는 seal 된 객체에는 프로퍼티를 추가할 수 없음
 */
export const canAttachErrorMeta = (error: unknown): error is object => isObject(error) && Object.isExtensible(error);

const defineHidden = (target: object, key: string, value: unknown) => {
  try {
    Object.defineProperty(target, key, { value, writable: true, configurable: true });
  } catch {}
};

const mergeApiMeta = (prev: ApiMeta = {}, next: ApiMeta = {}): ApiMeta => ({
  path: next.path ?? prev.path,
  method: next.method ?? prev.method,
  status: next.status ?? prev.status,
  errorCode: next.errorCode ?? prev.errorCode,
  track: next.track ?? prev.track,
  url: next.url ?? prev.url,
});

/** 기존값이 존재하는 경우 새 값 덮어쓰기(undefined는 무시) */
const mergeMeta = (prev: ErrorMeta | undefined, next: ErrorMeta): ErrorMeta => ({
  api: prev?.api || next.api ? mergeApiMeta(prev?.api, next.api) : undefined,
  auth: next.auth ?? prev?.auth,
  ui: next.ui ?? prev?.ui,
  render: next.render ?? prev?.render,
  perceived: next.perceived ?? prev?.perceived,
  extra: prev?.extra || next.extra ? { ...prev?.extra, ...next.extra } : undefined,
});

/**
 * 에러 메타 데이터를 에러 객체에 첨부(후위 우선 병합)
 */
export const attachErrorMeta = (error: unknown, meta: ErrorMeta): void => {
  if (!canAttachErrorMeta(error)) return;
  defineHidden(error, META_KEY, mergeMeta(asHolder(error).__ctrl_error_meta__, meta));
};

/** 에러 객체에 첨부된 메타데이터를 읽는다. 없으면 undefined */
export const readErrorMeta = (error: unknown): ErrorMeta | undefined => (isObject(error) ? asHolder(error).__ctrl_error_meta__ : undefined);

const CAUSE_DEPTH_LIMIT = 5;

/**
 * cause 체인을 따라가며 메타를 병합
 * 나중에 만들어지는 에러가 우선순위를 가진다
 */
export const readErrorMetaChain = (error: unknown, depth = 0): ErrorMeta | undefined => {
  if (depth > CAUSE_DEPTH_LIMIT || !isObject(error)) return undefined;

  const own = readErrorMeta(error);
  const fromCause = readErrorMetaChain((error as { cause?: unknown }).cause, depth + 1);
  if (!own && !fromCause) return undefined;

  return mergeMeta(fromCause, own ?? {});
};

/**
 * 이미 캡처된 에러인지 판정
 * 자체 플래그로 1차 판단, SDK 내부 마커로 2차 판단(SDK 버전에 의존성)
 */
export const hasBeenCaptured = (error: unknown): boolean => {
  if (!isObject(error)) return false;

  const holder = asHolder(error);
  return holder.__ctrl_captured__ === true || holder.__sentry_captured__ === true;
};

/**
 * 선후관계가 있는 에러들을 연결한다. 먼저 발생한 에러를 cause 로 연결
 */
export const linkErrorCause = (error: unknown, cause: unknown): void => {
  if (!(error instanceof Error) || error.cause !== undefined) return;
  defineHidden(error, "cause", cause);
};

export const markCaptured = (error: unknown): void => {
  if (!canAttachErrorMeta(error)) return;
  defineHidden(error, CAPTURED_KEY, true);
};
